import {
  COMPARISON_OPERATOR_IDS,
  EMAIL_TEMPLATE_STATUS_IDS,
  TASK_TRIGGER_LETTERS_GROUP_ID,
} from 'modules/enumerations/enumerationIds';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';

// Letter-sent task rules are ordinary Segment Level rules on the 'Letters' trigger group, whose one catalog table is
// Authorization.SentEmailTemplates (one row per letter send). Every send generates its own task once. The rule form
// manages two of their conditions itself:
//   Letter    - TemplateId Equals the letter's first-version template id (the id every send records)
//   Sent Date - EmailTemplateSentDate On Or After a cutoff date (optional; defaults to the day the rule is created so
//               letters sent earlier do not generate tasks)
// and the due date is always based on Sent Date. Any other conditions are on the authorization File table.
export const LETTER_FIELD_NAME = 'TemplateId';
export const SENT_DATE_FIELD_NAME = 'EmailTemplateSentDate';

export const isLetterRule = ruleData => isIdEqual(ruleData?.triggerServiceId, TASK_TRIGGER_LETTERS_GROUP_ID);

const isSentLettersField = field => field.tableName === 'Authorization.SentEmailTemplates';

export const getLetterFields = validTriggerFields => ({
  letterField: validTriggerFields.find(f => isSentLettersField(f) && f.fieldName === LETTER_FIELD_NAME) || null,
  sentDateField: validTriggerFields.find(f => isSentLettersField(f) && f.fieldName === SENT_DATE_FIELD_NAME) || null,
});

// Conditions of a letter rule, split into the two the form manages and the user's additional (File) conditions.
// Matched by field name: the File table has no TemplateId or EmailTemplateSentDate field.
export const splitLetterConditions = conditions => {
  const letterCondition = conditions.find(c => c.triggerFieldName === LETTER_FIELD_NAME) || null;
  const cutoffCondition = conditions.find(c => c.triggerFieldName === SENT_DATE_FIELD_NAME
    && isIdEqual(c.comparisonOperatorId, COMPARISON_OPERATOR_IDS.GREATER_THAN_OR_EQUAL)) || null;
  const additionalConditions = conditions.filter(c => c !== letterCondition && c !== cutoffCondition);
  return { letterCondition, cutoffCondition, additionalConditions };
};

export const buildLetterCondition = (letterField, rootTemplateId) => ({
  validTriggerFieldId: letterField.validTriggerFieldId,
  triggerFieldName: LETTER_FIELD_NAME,
  comparisonOperatorId: COMPARISON_OPERATOR_IDS.EQUALS,
  comparisonValue: String(rootTemplateId),
});

export const buildCutoffCondition = (sentDateField, cutoffDate) => ({
  validTriggerFieldId: sentDateField.validTriggerFieldId,
  triggerFieldName: SENT_DATE_FIELD_NAME,
  comparisonOperatorId: COMPARISON_OPERATOR_IDS.GREATER_THAN_OR_EQUAL,
  comparisonValue: cutoffDate,
});

// Recombines the managed and additional conditions into the rule's condition list, numbered in order
export const joinLetterConditions = ({ letterCondition, cutoffCondition, additionalConditions }) =>
  [letterCondition, cutoffCondition, ...additionalConditions]
    .filter(Boolean)
    .map((c, i) => ({ ...c, conditionOrder: i + 1 }));

// One option per letter (template version family), named after the version in use. Sends record the first version's
// id, so that is the option's id. Only letters with an Active version are offered, plus the rule's current letter so a
// saved rule still shows it after the letter is retired.
export const getLetterOptions = (emailTemplates, currentRootTemplateId) => {
  const families = new Map();
  (emailTemplates || []).forEach((template) => {
    const rootTemplateId = template.parentEmailTemplateId > 0 ? template.parentEmailTemplateId : template.templateId;
    const family = families.get(rootTemplateId) || [];
    family.push(template);
    families.set(rootTemplateId, family);
  });

  const options = [];
  families.forEach((versions, rootTemplateId) => {
    const activeVersion = versions.find(v => isIdEqual(v.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.ACTIVE));
    const isCurrent = String(rootTemplateId) === String(currentRootTemplateId);
    if (!activeVersion && !isCurrent) {
      return;
    }
    const shownVersion = activeVersion || [...versions].sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))[0];
    options.push({
      rootTemplateId,
      name: shownVersion.templateName || `Letter ${rootTemplateId}`,
      serviceId: shownVersion.serviceId,
      regardingId: shownVersion.regardingId,
      isActive: !!activeVersion,
    });
  });
  return options;
};
